/**
 * SheetsApplication - Main application class for SheetsApp_v2
 * Extends BaseApplication to provide spreadsheet functionality
 * 
 * This demonstrates how the unified architecture works for grid-based apps!
 */

import * as Y from 'yjs';
import { BaseApplication } from '../../../services/baseApplication/BaseApplication';
import { AppConfig, SerializedNode } from '../../../services/baseApplication/types';
import { SHEETS_APP_CONFIG, isCellClassName } from './SheetsConfig';
import { FormulaEngine, FormulaResult } from './FormulaEngine';
import { createCollaborationProvider } from '../../../services/collaboration';
import { authService } from '../../../services/auth/authService';
import { entityService } from '../../../services/entityService';
import { generateUserColor } from '../../shared/appHelpers';

export class SheetsApplication extends BaseApplication {
  private formulaEngine: FormulaEngine;
  private calculationOrder: string[]; // Cells in dependency order
  private yrows: Y.Map<any> | null = null;  // ✅ NEW: Row-sparse storage
  // ✅ collaborationProvider inherited from BaseApplication

  constructor() {
    super();
    this.formulaEngine = new FormulaEngine();
    this.calculationOrder = [];
  }
  
  /**
   * ✅ OVERRIDE: Initialize for AI commands
   * Called by getApplicationForEntity() for entity_read/write
   */
  async initialize(entityId: string): Promise<void> {
    console.log('[SheetsApplication] AI Initialize:', entityId);
    
    // ✅ CRITICAL: Load entity to get saved state!
    const entity = await entityService.get(entityId);
    
    // Call parent initialize (creates Y.Doc, sets entityId, etc.)
    await super.initialize(entityId);
    
    // ✅ FIXED (Issue #4): Use BaseApplication's loadBase64State
    if (entity?.skeleton?.base64 && this.ydoc) {
      try {
        console.log('[SheetsApplication] 🔄 Loading saved state from base64...');
        this.loadBase64State(entity.skeleton.base64);
        console.log('[SheetsApplication] ✅ Saved state loaded');
      } catch (error) {
        console.error('[SheetsApplication] ❌ Failed to load saved state:', error);
      }
    }
    
    // ✅ Get yxml for backward compatibility and initialize row-sparse storage
    if (this.ydoc) {
      this.yxml = this.ydoc.getXmlFragment('prosemirror');
      this.yrows = this.ydoc.getMap('rows');  // ✅ NEW: Row-sparse storage
      
      console.log('[SheetsApplication] 🔍 After loading state, storage:', {
        yxmlLength: this.yxml.length,
        yrowsSize: this.yrows.size,
        hasBase64: !!entity?.skeleton?.base64,
        ydocGuid: this.ydoc.guid
      });
      
      // Migrate from old dense table to row-sparse if needed
      if (this.yxml.length > 0 && this.yrows.size === 0) {
        console.log('[SheetsApplication] 🔄 Migrating from dense table to row-sparse...');
        this.migrateToRowSparse();
      }
      
      // Create empty sheet if no data (no old table, no rows)
      if (this.yxml.length === 0 && this.yrows.size === 0) {
        console.log('[SheetsApplication] 🚀 Initializing empty row-sparse sheet...');
        // Row-sparse: No upfront initialization needed! Rows created on-demand.
        console.log('[SheetsApplication] ✅ Row-sparse sheet ready (0 rows, will create on-demand)');
      }
    }
  }
  
  /**
   * Load spreadsheet from backend (for React UI)
   * ✅ UPDATED: Now uses BaseApplication unified persistence pattern
   */
  async load(entityId: string, options: { offline?: boolean } = {}): Promise<void> {
    console.log('[SheetsApplication] Loading spreadsheet:', entityId);
    
    const entity = await entityService.get(entityId);
    if (!entity) throw new Error(`Spreadsheet not found: ${entityId}`);
    
    // ✅ Set entityId (used by BaseApplication.setupAutoSave)
    this.entityId = entityId;
    
    // ✅ NEW PATTERN: Create own Y.Doc (self-contained)
    this.ydoc = new Y.Doc();
    console.log('[SheetsApplication] Created ydoc:', this.ydoc.guid);
    
    // ✅ NEW PATTERN: Load base64 state directly (if exists) with error handling
    if (entity.skeleton?.base64 && this.ydoc) {
      console.log('[SheetsApplication] Loading Y.js state from base64...');
      try {
        this.loadBase64State(entity.skeleton.base64);
        console.log('[SheetsApplication] ✅ Y.js state loaded');
      } catch (error) {
        console.error('[SheetsApplication] ❌ Failed to load Y.js state:', error);
        this.emit('loadError', { 
          error, 
          message: 'Failed to load spreadsheet data. Starting with empty spreadsheet.' 
        });
        // Continue with empty spreadsheet rather than failing completely
        console.log('[SheetsApplication] ⚠️ Continuing with empty spreadsheet');
      }
    }
    
    if (this.ydoc) {
      this.yxml = this.ydoc.getXmlFragment('prosemirror');
      this.yrows = this.ydoc.getMap('rows');  // ✅ Row-sparse storage
      
      console.log('[SheetsApplication] 🔍 Checking storage state:', {
        yxmlLength: this.yxml.length,
        yrowsSize: this.yrows.size,
        hasBase64: !!entity.skeleton?.base64,
        hasInitialCells: !!entity.skeleton?.initialCells,
        csvImport: !!entity.skeleton?.csvImport,
      });
      
      // ✅ Migrate from old dense table to row-sparse if needed
      if (this.yxml.length > 0 && this.yrows.size === 0) {
        console.log('[SheetsApplication] 🔄 Migrating from dense table to row-sparse...');
        this.migrateToRowSparse();
      }
      
      // ✅ CSV IMPORT: Load cells into row-sparse storage
      if (this.yrows.size === 0 && !entity.skeleton?.base64 && entity.skeleton?.initialCells) {
        console.log('[SheetsApplication] 📊 Loading CSV data into row-sparse storage:', {
          cellCount: entity.skeleton.initialCells.length,
          dimensions: entity.skeleton.dimensions
        });
        
        this.loadCellsFromCSVImportRowSparse(entity.skeleton.initialCells);
        console.log('[SheetsApplication] ✅ CSV cells loaded, rows:', this.yrows.size);
      }
      
      // Row-sparse: No upfront initialization for empty sheets!
      // Rows will be created on-demand when user types
      if (this.yrows.size === 0 && this.yxml.length === 0) {
        console.log('[SheetsApplication] ✅ Empty row-sparse sheet ready (rows created on-demand)');
      }
      
      // ✅ FIX (Issue #5): Setup auto-save BEFORE building indices
      // This ensures migrations get auto-saved
      console.log('[SheetsApplication] 🔧 Setting up auto-save...');
      this.setupAutoSave();
      console.log('[SheetsApplication] ✅ Auto-save enabled');
      
      await this.buildCoreIndices();
    }
    
    // ✅ NEW PATTERN: Optional WebSocket collaboration (real-time sync, not required for persistence)
    if (!options.offline) {
      await this.setupCollaborationWithDoc(entityId, this.ydoc!);
    }
    
    console.log('[SheetsApplication] ✅ Spreadsheet loaded');
  }
  
  /**
   * ✅ OVERRIDE: Build indices from row-sparse storage
   * IndexManager expects data in yxml, but we store in yrows
   */
  protected async buildCoreIndices(): Promise<void> {
    console.log('[SheetsApplication] Building indices from row-sparse storage');
    
    if (!this.ydoc || !this.yrows) {
      console.warn('[SheetsApplication] No ydoc or yrows, skipping indices');
      return;
    }
    
    console.log('[SheetsApplication] 🔍 Row-sparse state:', {
      yrowsSize: this.yrows.size,
      yxmlLength: this.yxml?.length || 0
    });
    
    // ✅ Manually build indices from yrows (not yxml!)
    this.byIdIndex.clear();
    this.byClassIndex.clear();
    this.fullTextIndex.clear();
    
    let totalCells = 0;
    let filledCells = 0;
    
    // Walk all stored rows
    this.yrows.forEach((row, key) => {
      const rowNum = parseInt(key, 10);
      if (!(row instanceof Y.XmlElement)) return;
      
      // Walk all cells in row
      for (let c = 0; c < row.length && c < 20; c++) {
        const cell = row.get(c);
        if (!(cell instanceof Y.XmlElement)) continue;
        
        totalCells++;
        
        const cellId = cell.getAttribute('id');
        if (!cellId) {
          console.warn(`[SheetsApplication] Cell at ${rowNum},${c} has no ID!`);
          continue;
        }
        
        // Extract cell value
        const para = cell.get(0);
        const value = (para instanceof Y.XmlElement && para.get(0) instanceof Y.XmlText) 
          ? (para.get(0) as Y.XmlText).toString() 
          : '';
        
        if (value) {
          filledCells++;
        }
        
        // ✅ Index by ID (required for entity_read/write!)
        // Create a pseudo-node structure for compatibility
        const pseudoNode: any = {
          type: { name: 'table_cell' },
          attrs: {
            id: cellId,
            value: value,
            semantic: {
              cellRef: this.getCellRef(rowNum, c),
              cellType: cell.getAttribute('cellType') || 'text',
              formula: cell.getAttribute('formula') || undefined,
              row: rowNum,
              col: c
            },
            // Include formatting
            bold: cell.getAttribute('bold') || undefined,
            italic: cell.getAttribute('italic') || undefined,
            fontSize: cell.getAttribute('fontSize') || undefined,
            textColor: cell.getAttribute('textColor') || undefined,
            backgroundColor: cell.getAttribute('backgroundColor') || undefined,
            textAlign: cell.getAttribute('textAlign') || undefined,
          },
          textContent: value
        };
        
        this.byIdIndex.set(cellId, pseudoNode);
        
        // Index by cell type
        const cellType = cell.getAttribute('cellType') || 'text';
        if (!this.byClassIndex.has(cellType)) {
          this.byClassIndex.set(cellType, new Set());
        }
        this.byClassIndex.get(cellType)!.add(cellId);
        
        // Full-text index for search
        if (value) {
          const words = value.toLowerCase().split(/\s+/);
          words.forEach((word: string) => {
            if (word.length > 0) {
              if (!this.fullTextIndex.has(word)) {
                this.fullTextIndex.set(word, new Set());
              }
              this.fullTextIndex.get(word)!.add(cellId);
            }
          });
        }
      }
    });
    
    console.log('[SheetsApplication] ✅ Row-sparse indices built:', {
      storedRows: this.yrows.size,
      totalCells,
      filledCells,
      indexedNodes: this.byIdIndex.size,
      cellTypes: this.byClassIndex.size,
      searchWords: this.fullTextIndex.size
    });
  }
  
  /**
   * Set up collaboration with proper listener cleanup
   * ✅ FIX: Prevent memory leaks by cleaning up listeners and timeout
   */
  private async setupCollaborationWithDoc(entityId: string, ydoc: Y.Doc): Promise<void> {
    const authUser = authService.getCurrentUser();
    if (!authUser) {
      throw new Error('Not authenticated');
    }
    
    const currentUser = {
      id: authUser.id,
      name: authUser.name || authUser.email || 'User',
      color: generateUserColor(authUser.id)
    };
    
    // ✅ CRITICAL: Pass pre-loaded doc to provider.
    // Room name must be prefixed "doc:{entityId}" to match the tool-runtime's
    // getRoomName() convention and localFirstSyncService's room naming.
    this.collaborationProvider = createCollaborationProvider({
      documentId: `doc:${entityId}`,
      user: currentUser,
      websocketUrl: this.getWebSocketUrl(),
      authToken: '',
      offline: false,
      doc: ydoc  // ✅ Use our pre-loaded doc!
    });
    
    this.collaborationProvider.connect();
    
    // ✅ FIX: Properly cleanup timeout and event listeners to prevent memory leaks
    await new Promise<void>((resolve) => {
      let timeoutId: NodeJS.Timeout | null = null;
      let syncedHandler: (() => void) | null = null;
      let errorHandler: ((error: Error) => void) | null = null;
      
      // ✅ Cleanup function to remove all listeners and timeout
      const cleanup = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (syncedHandler && this.collaborationProvider) {
          this.collaborationProvider.off('synced', syncedHandler);
          syncedHandler = null;
        }
        if (errorHandler && this.collaborationProvider) {
          this.collaborationProvider.off('error', errorHandler);
          errorHandler = null;
        }
      };
      
      // Timeout handler
      timeoutId = setTimeout(() => {
        console.warn('[SheetsApplication] Sync timeout - proceeding anyway');
        cleanup();
        resolve();
      }, 5000);
      
      // Success handler
      syncedHandler = () => {
        console.log('[SheetsApplication] ✅ Spreadsheet synced');
        cleanup();
        resolve();
      };
      
      // Error handler
      errorHandler = (error: Error) => {
        console.error('[SheetsApplication] Sync error:', error);
        cleanup();
        resolve(); // Don't fail - continue with local state
      };
      
      // Attach handlers
      this.collaborationProvider!.on('synced', syncedHandler);
      this.collaborationProvider!.on('error', errorHandler);
    });
    
    console.log('[SheetsApplication] ✅ Collaboration ready');
  }
  
  /**
   * ✅ REFACTORED: Get or create row on-demand (row-sparse, transaction-safe)
   */
  private getOrCreateRow(rowNum: number): Y.XmlElement {
    if (!this.yrows) {
      throw new Error('Row storage not initialized');
    }
    
    const key = String(rowNum);
    let row = this.yrows.get(key);
    
    if (!row && this.ydoc) {
      // ✅ FIX: Wrap row creation in transaction with double-check
      this.ydoc.transact(() => {
        // Double-check inside transaction (race condition safety)
        row = this.yrows!.get(key);
        if (!row) {
          // Create row with 20 cells
          const timestamp = Date.now();
          row = new Y.XmlElement('table_row');
          row.setAttribute('id', `node_row_${rowNum}_${timestamp}_${Math.random().toString(36).substr(2, 9)}`);
          
          const cells = [];
          for (let c = 0; c < 20; c++) {
            const cell = new Y.XmlElement('table_cell');
            cell.setAttribute('id', `node_cell_${rowNum}_${c}_${timestamp}_${Math.random().toString(36).substr(2, 9)}`);
            cell.setAttribute('cellType', 'text');
            
            const para = new Y.XmlElement('paragraph');
            para.setAttribute('id', `node_para_${rowNum}_${c}_${timestamp}_${Math.random().toString(36).substr(2, 9)}`);
            
            cell.insert(0, [para]);
            cells.push(cell);
          }
          
          row.insert(0, cells);
          this.yrows!.set(key, row);
          
          console.log(`[SheetsApplication] ✅ Created row ${rowNum} on-demand (20 cells) via transaction`);
        }
      }, this.ydoc.clientID);
      
      // Re-fetch after transaction
      row = this.yrows.get(key);
    }
    
    if (!row) {
      throw new Error(`[SheetsApplication] Failed to get or create row ${rowNum}`);
    }
    
    return row;
  }
  
  /**
   * ✅ NEW: Check if row is empty (all cells have no content)
   */
  private isRowEmpty(row: Y.XmlElement): boolean {
    for (let c = 0; c < row.length; c++) {
      const cell = row.get(c);
      if (!(cell instanceof Y.XmlElement)) continue;
      
      const value = this.extractCellValue(cell);
      if (value && value.trim()) {
        return false;  // Has content
      }
    }
    return true;  // All cells empty
  }
  
  /**
   * ✅ NEW: Migrate from old dense table to row-sparse
   */
  private migrateToRowSparse(): void {
    if (!this.yxml || !this.yrows) return;
    
    const table = this.yxml.get(0);
    if (!(table instanceof Y.XmlElement) || table.nodeName !== 'table') {
      console.warn('[SheetsApplication] No table found in yxml for migration');
      return;
    }
    
    let migratedRows = 0;
    
    // Extract rows with data
    for (let r = 0; r < table.length; r++) {
      const row = table.get(r);
      if (!(row instanceof Y.XmlElement)) continue;
      
      // Check if row has any data
      if (!this.isRowEmpty(row)) {
        this.yrows.set(String(r), row);
        migratedRows++;
      }
    }
    
    // Delete old dense table
    this.yxml.delete(0, this.yxml.length);
    
    console.log(`[SheetsApplication] ✅ Migrated to row-sparse: ${migratedRows} rows with data (from dense table)`);
  }
  
  /**
   * ✅ NEW: Load cells from CSV import into row-sparse storage
   */
  private loadCellsFromCSVImportRowSparse(initialCells: any[]): void {
    if (!this.yrows) {
      console.error('[SheetsApplication] ❌ No yrows, cannot load CSV!');
      return;
    }
    
    if (!initialCells || !Array.isArray(initialCells) || initialCells.length === 0) {
      console.warn('[SheetsApplication] Empty or invalid initialCells');
      return;
    }
    
    // Group cells by row
    const rowsMap = new Map<number, any[]>();
    initialCells.forEach(cell => {
      if (cell && typeof cell.row === 'number' && typeof cell.col === 'number') {
        if (!rowsMap.has(cell.row)) {
          rowsMap.set(cell.row, []);
        }
        rowsMap.get(cell.row)!.push(cell);
      }
    });
    
    const timestamp = Date.now();
    
    // Create only rows that have data
    rowsMap.forEach((cells, rowNum) => {
      const row = new Y.XmlElement('table_row');
      row.setAttribute('id', `node_row_${rowNum}_${timestamp}_${Math.random().toString(36).substr(2, 9)}`);
      
      const cellElements = [];
      for (let c = 0; c < 20; c++) {
        const cellData = cells.find(cell => cell.col === c);
        
        const cell = new Y.XmlElement('table_cell');
        cell.setAttribute('id', `node_cell_${rowNum}_${c}_${timestamp}_${Math.random().toString(36).substr(2, 9)}`);
        cell.setAttribute('cellType', cellData?.type || 'text');
        
        if (cellData?.formula) {
           cell.setAttribute('formula', cellData.formula);   
        }
        
        const para = new Y.XmlElement('paragraph');
        para.setAttribute('id', `node_para_${rowNum}_${c}_${timestamp}_${Math.random().toString(36).substr(2, 9)}`);
        
        if (cellData?.value) {
          const text = new Y.XmlText();
          text.insert(0, String(cellData.value));
          para.insert(0, [text]);
        }
        
        cell.insert(0, [para]);
        cellElements.push(cell);
      }
      
      row.insert(0, cellElements);
      if (this.yrows) {
        this.yrows.set(String(rowNum), row);
      }
    });
    
    console.log(`[SheetsApplication] ✅ CSV loaded into row-sparse: ${rowsMap.size} rows from ${initialCells.length} cells`);
  }
  
  /**
   * Initialize empty spreadsheet (LEGACY - kept for compatibility)
   * Row-sparse version: Does nothing, rows created on-demand
   */
  public initializeEmptySpreadsheet(): void {
    // Row-sparse: No upfront table creation!
    // Rows will be created on-demand when user writes data
    console.log('[SheetsApplication] ✅ Row-sparse mode: No upfront table creation (rows created on-demand)');
  }
  
  /**
   * Load cells from CSV import data into row-sparse storage
   * ✅ PERFORMANCE: Only creates rows that have data
   */
  private loadCellsFromCSVImport(initialCells: any[], dimensions: any): void {
    console.log('[SheetsApplication] ⚠️ Using legacy loadCellsFromCSVImport, redirecting to row-sparse...');
    this.loadCellsFromCSVImportRowSparse(initialCells);
  }
  
  /**
   * ✅ OVERRIDE: Custom entity_read for sheets
   * Returns optimized format: only cells with data, A1 notation, clean structure
   */
  async handleEntityRead(selector: any, options: any = {}): Promise<any> {
    console.log('[SheetsApplication] handleEntityRead (optimized for sheets):', selector);
    
    await this.buildCoreIndices();
    
    // ✅ For 'all', return sheet-optimized format
    if (selector.all) {
      return this.getSheetSummaryForAI();
    }
    
    // For specific queries, use parent implementation
    return super.handleEntityRead(selector, options);
  }
  
  /**
   * Get sheet summary for AI (only cells with data!)
   * ✅ Uses row-sparse storage for efficiency
   */
  private getSheetSummaryForAI(): any {
    console.log('[SheetsApplication] Building sheet summary for AI (row-sparse)...');
    
    const dataCells: any[] = [];
    
    // Check both old (yxml) and new (yrows) storage for migration
    if (!this.yrows || this.yrows.size === 0) {
      // Check if we have old table format
      if (this.yxml && this.yxml.length > 0) {
        console.log('[SheetsApplication] ⚠️ Found old table format, migrating...');
        this.migrateToRowSparse();
      }
      
      if (!this.yrows || this.yrows.size === 0) {
        return {
          nodes: [{
            type: 'sheet_summary',
            id: 'sheet_summary',
            sheetInfo: {rows: 0, cols: 0, totalCells: 0, filledCells: 0},
            cells: []
          }],
          wordCount: 0
        };
      }
    }
    
    // ✅ Row-sparse: Calculate dimensions from stored rows
    const maxRowNum = Array.from(this.yrows.keys()).reduce((max, key) => {
      const rowNum = parseInt(key, 10);
      return Math.max(max, rowNum);
    }, -1);
    
    const actualRows = maxRowNum >= 0 ? maxRowNum + 1 : 0;
    const actualCols = 20;  // Fixed 20 columns per row
    const totalCells = this.yrows.size * actualCols;
    
    // ✅ Row-sparse: Only iterate stored rows (no empty rows!)
    this.yrows.forEach((row, key) => {
      const r = parseInt(key, 10);
      if (!(row instanceof Y.XmlElement)) return;
      
      for (let c = 0; c < row.length && c < 20; c++) {
        const cell = row.get(c);
        if (!(cell instanceof Y.XmlElement)) continue;
        
        // Extract value
        const value = this.extractCellValue(cell);
        
        // ✅ ONLY include cells with data!
        if (value && value.trim()) {
          dataCells.push({
            row: r,
            col: c,
            cellRef: this.getCellRef(r, c),
            value: value.trim(),
            type: cell.getAttribute('cellType') || 'text',
            formula: cell.getAttribute('formula') || undefined,
            id: cell.getAttribute('id'),
            formatting: this.getCellFormatting(cell)
          });
        }
      }
    });
    
    console.log('[SheetsApplication] ✅ Sheet summary (row-sparse):', {
      storedRows: this.yrows.size,
      maxRowNum,
      actualRows,
      actualCols,
      totalCells,
      filledCells: dataCells.length
    });
    
    // ✅ CRITICAL: Return format must match entity_read contract!
    const summaryNode = {
      type: 'sheet_summary',
      id: 'sheet_summary',
      sheetInfo: {
        rows: actualRows,
        cols: actualCols,
        totalCells: totalCells,
        filledCells: dataCells.length,
        storedRows: this.yrows.size  // ✅ NEW: Show sparse storage efficiency
      },
      cells: dataCells  // All cells with data
    };
    
    return {
      nodes: [summaryNode],
      wordCount: dataCells.reduce((sum, cell) => sum + (cell.value?.split(' ').length || 0), 0)
    };
  }
  
  /**
   * Convert row/col to A1 notation (e.g., row 8, col 1 → "B9")
   */
  private getCellRef(row: number, col: number): string {
    const colLetter = String.fromCharCode(65 + col);  // 0→A, 1→B, 2→C...
    return `${colLetter}${row + 1}`;  // Row 0 is "1", so B9 is row 8, col 1
  }
  
  /**
   * Extract text value from cell element
   */
  private extractCellValue(cell: Y.XmlElement): string {
    // Cell structure: table_cell → paragraph → text
    const para = cell.get(0);
    if (!(para instanceof Y.XmlElement)) return '';
    
    const textNode = para.get(0);
    if (textNode instanceof Y.XmlText) {
      return textNode.toString();
    }
    
    return '';
  }
  
  /**
   * Handle formal batch operation
   * AI sends: { "operations": [{ "target": {...}, "attrs": {"A1": "val"} }, ...] }
   */
  private async handleFormalBatchOperation(operations: any[]): Promise<any> {
    console.log('[SheetsApplication] 🔧 handleFormalBatchOperation:', {
      operationCount: operations.length
    });
    
    // Collect all cell updates from all operations
    const allCellUpdates: Record<string, any> = {};
    
    for (const op of operations) {
      if (!op.attrs) continue;
      
      // Check if this operation's attrs contain cell references
      const cellRefPattern = /^[A-Z]+\d+$/;
      const attrsKeys = Object.keys(op.attrs);
      
      for (const key of attrsKeys) {
        if (cellRefPattern.test(key)) {
          allCellUpdates[key] = op.attrs[key];
          console.log(`[SheetsApplication] 📍 Found cell update in batch: ${key} = ${op.attrs[key]}`);
        }
      }
    }
    
    if (Object.keys(allCellUpdates).length === 0) {
      console.warn('[SheetsApplication] ⚠️ No cell references found in batch operations');
      return {
        affectedNodes: [],
        stats: { nodesModified: 0, errors: 0 }
      };
    }
    
    console.log('[SheetsApplication] 🎯 Extracted cell updates from batch:', {
      cellCount: Object.keys(allCellUpdates).length,
      cells: Object.keys(allCellUpdates)
    });
    
    // Use the same batch update handler
    return this.handleBatchCellUpdate(allCellUpdates);
  }
  
  /**
   * Handle batch cell update from attrs object
   * AI sends: { "A1": "value1", "B1": "value2", ... }
   */
  private async handleBatchCellUpdate(attrs: Record<string, any>): Promise<any> {
    console.log('[SheetsApplication] 🔧 handleBatchCellUpdate (row-sparse):', {
      cellCount: Object.keys(attrs).length,
      cells: Object.keys(attrs)
    });
    
    if (!this.yrows) {
      throw new Error('Row storage not initialized');
    }
    
    const affectedNodes: string[] = [];
    let successCount = 0;
    let errorCount = 0;
    
    // ✅ CRITICAL FIX: Validate cell reference format BEFORE processing
    const cellRefPattern = /^[A-Z]+\d+$/;
    
    // ✅ Do all updates in ONE transaction (atomic + faster)
    this.ydoc!.transact(() => {
      for (const [cellRef, value] of Object.entries(attrs)) {
        try {
          // ✅ Validate cell reference format first
          if (!cellRefPattern.test(cellRef)) {
            console.warn(`[SheetsApplication] ⚠️ Skipping non-cell-ref key: ${cellRef}`);
            errorCount++;
            continue;
          }
          
          const position = this.parseCellRef(cellRef);
          if (!position) {
            console.warn(`[SheetsApplication] ⚠️ Invalid cell ref: ${cellRef}`);
            errorCount++;
            continue;
          }
          
          const {row, col} = position;
          
          // ✅ Get or create row on-demand
          const tableRow = this.getOrCreateRow(row);
          
          if (col >= 20) {
            throw new Error(`Column ${col} out of bounds (max 20)`);
          }
          
          const cell = tableRow.get(col) as Y.XmlElement;
          const para = cell.get(0) as Y.XmlElement;
          
          // Update text
          if (para.length > 0) {
            const textNode = para.get(0);
            if (textNode instanceof Y.XmlText) {
              textNode.delete(0, textNode.length);
              textNode.insert(0, String(value));
            }
          } else {
            const text = new Y.XmlText();
            text.insert(0, String(value));
            para.insert(0, [text]);
          }
          
          affectedNodes.push(cell.getAttribute('id') || '');
          successCount++;
          
          console.log(`[SheetsApplication] ✅ Updated ${cellRef} = ${value}`);
        } catch (error) {
          console.error(`[SheetsApplication] ❌ Failed to update ${cellRef}:`, error);
          errorCount++;
        }
      }
    }, this.ydoc!.clientID);  // ✅ FIX: Use clientID origin for undo tracking
    
    // ✅ Auto-save handled by BaseApplication observer
    console.log('[SheetsApplication] 🎉 Batch update (row-sparse) complete:', {
      total: Object.keys(attrs).length,
      success: successCount,
      errors: errorCount
    });
    
    return {
      affectedNodes,
      stats: {
        nodesModified: successCount,
        errors: errorCount
      }
    };
  }
  
  /**
   * Parse cell reference (A1 notation) to row/col
   * ✅ Validates against actual table dimensions, not hardcoded limits
   * ✅ Supports multi-letter columns (A-Z, AA-ZZ, etc.)
   */
  private parseCellRef(ref: string): {row: number, col: number} | null {
    const match = ref.match(/^([A-Z]+)(\d+)$/);
    if (!match) return null;
    
    // ✅ FIX: Properly handle multi-letter columns (AA, AB, etc.)
    const colStr = match[1];
    let col = 0;
    for (let i = 0; i < colStr.length; i++) {
      col = col * 26 + (colStr.charCodeAt(i) - 65 + 1);
    }
    col -= 1; // Make 0-indexed (A=0, B=1, ..., Z=25, AA=26, etc.)
    
    const row = parseInt(match[2]) - 1;        // 1→0, 2→1, etc.
    
    if (row < 0 || col < 0) {
      return null;  // Negative indices are invalid
    }
    
    // ✅ Row-sparse: Check bounds (500 rows x 20 cols)
    if (row >= 500 || col >= 20) {
      console.warn(`[SheetsApplication] Cell ${ref} out of bounds (max 500x20)`);
      return null;
    }
    
    return {row, col};
  }
  
  /**
   * Update cell at specific position (row-sparse)
   * ✅ Creates row on-demand if it doesn't exist
   */
  private async updateCellAtPosition(row: number, col: number, content: string): Promise<any> {
    console.log('[SheetsApplication] 🎯 Updating cell at position (row-sparse):', {row, col, content});
    
    if (!this.yrows) {
      throw new Error('Row storage not initialized');
    }
    
    // ✅ Get or create row on-demand
    const tableRow = this.getOrCreateRow(row);
    
    if (col >= 20) {
      throw new Error(`Column ${col} out of bounds (max 20)`);
    }
    
    const cell = tableRow.get(col) as Y.XmlElement;
    if (!cell) {
      throw new Error(`Cell ${row},${col} not found after row creation`);
    }
    
    const para = cell.get(0) as Y.XmlElement;
    
    console.log('[SheetsApplication] ✅ Found/created cell, updating text...');
    
    // Update text content
    this.ydoc!.transact(() => {
      if (para.length > 0) {
        const textNode = para.get(0);
        if (textNode instanceof Y.XmlText) {
          textNode.delete(0, textNode.length);
          textNode.insert(0, content);
        }
      } else {
        const text = new Y.XmlText();
        text.insert(0, content);
        para.insert(0, [text]);
      }
    }, this.ydoc!.clientID);  // ✅ FIX: Use clientID origin for undo tracking
    
    // ✅ Auto-save handled by BaseApplication observer
    console.log('[SheetsApplication] ✅ Cell updated at position (row-sparse)!');
    
    return {
      affectedNodes: [cell.getAttribute('id')],
      stats: {nodesModified: 1}
    };
  }
  
  /**
   * Get cell formatting attributes
   */
  private getCellFormatting(cell: Y.XmlElement): any {
    const formatting: any = {};
    
    // Extract all formatting attributes
    if (cell.getAttribute('bold') === 'true') formatting.bold = true;
    if (cell.getAttribute('italic') === 'true') formatting.italic = true;
    if (cell.getAttribute('underline') === 'true') formatting.underline = true;
    if (cell.getAttribute('strikethrough') === 'true') formatting.strikethrough = true;
    
    const textColor = cell.getAttribute('textColor');
    if (textColor) formatting.textColor = textColor;
    
    const backgroundColor = cell.getAttribute('backgroundColor');
    if (backgroundColor) formatting.backgroundColor = backgroundColor;
    
    const fontSize = cell.getAttribute('fontSize');
    if (fontSize) formatting.fontSize = fontSize;
    
    const textAlign = cell.getAttribute('textAlign');
    if (textAlign) formatting.textAlign = textAlign;
    
    // Return undefined if no formatting (cleaner JSON)
    return Object.keys(formatting).length > 0 ? formatting : undefined;
  }
  
  /**
   * ✅ OVERRIDE: Sheets-specific entity_write
   * Updates cell TEXT content, not just attributes!
   */
  async handleEntityWrite(params: any): Promise<any> {
    console.log('[SheetsApplication] handleEntityWrite (sheets-specific):', {
      operation: params.operation,
      hasContent: !!params.content,
      hasAttrs: !!params.attrs,
      hasTarget: !!params.target,
      attrsKeys: params.attrs ? Object.keys(params.attrs) : [],
      attrsValues: params.attrs
    });
    
    // ✅ NEW: Normalize operations (insert/replace → update for sheets)
    let normalizedOperation = params.operation;
    if (normalizedOperation === 'insert' || normalizedOperation === 'replace') {
      console.log(`[SheetsApplication] Normalizing operation ${normalizedOperation} → update (sheets auto-expand)`);
      normalizedOperation = 'update';
    }
    
    const normalizedParams = {
      ...params,
      operation: normalizedOperation
    };
    
    // ✅ NEW: Handle sheet-specific delete operations
    if (normalizedParams.operation === 'delete' && normalizedParams.target) {
      console.log('[SheetsApplication] 🗑️ Handling sheet-specific delete operation');
      
      const { target } = normalizedParams;
      const deletedCells: string[] = [];
      let cellsCleared = 0;
      
      // Wrap in transaction
      this.ydoc?.transact(() => {
        // Handle cellRef (single or range)
        if (target.cellRef) {
          console.log('[SheetsApplication] Deleting cellRef:', target.cellRef);
          const cellRefs = this.expandCellRange(target.cellRef);
          
          for (const ref of cellRefs) {
            if (this.clearCell(ref)) {
              deletedCells.push(ref);
              cellsCleared++;
            }
          }
        }
        
        // Handle row deletion
        if (typeof target.row === 'number') {
          console.log('[SheetsApplication] Deleting row:', target.row);
          const cleared = this.clearRow(target.row);
          deletedCells.push(`row_${target.row}`);
          cellsCleared += cleared;
        }
        
        // Handle column deletion
        if (target.column) {
          console.log('[SheetsApplication] Deleting column:', target.column);
          const cleared = this.clearColumn(target.column);
          deletedCells.push(`col_${target.column}`);
          cellsCleared += cleared;
        }
        
        // Handle specific cells array
        if (target.cells && Array.isArray(target.cells)) {
          console.log('[SheetsApplication] Deleting specific cells:', target.cells);
          for (const ref of target.cells) {
            if (this.clearCell(ref)) {
              deletedCells.push(ref);
              cellsCleared++;
            }
          }
        }
      }, 'sheet_delete');
      
      console.log('[SheetsApplication] ✅ Delete complete:', { 
        deletedCells: deletedCells.length,
        cellsCleared 
      });
      
      // Return result without calling parent
      if (deletedCells.length === 0) {
        return {
          status: 'error',
          format: 'plaintext',
          message: 'No cells found to delete. The specified range may be empty or out of bounds.',
          error: {
            code: 'NO_CELLS_FOUND',
            category: 'execution',
            details: { target }
          }
        };
      }
      
      return {
        status: 'success',
        format: 'plaintext',
        message: `Deleted ${cellsCleared} cell${cellsCleared !== 1 ? 's' : ''} from sheet`,
        data: {
          affectedNodes: deletedCells,
          cellsCleared
        }
      };
    }
    
    // Extract content from either params.content OR params.attrs
    let content = normalizedParams.content;
    
    // ✅ CRITICAL: AI might send value in attrs instead of content!
    if (!content && normalizedParams.attrs) {
      console.log('[SheetsApplication] 🔍 ATTRS KEYS:', Object.keys(normalizedParams.attrs));
      console.log('[SheetsApplication] 🔍 ATTRS FULL JSON:', JSON.stringify(normalizedParams.attrs, null, 2));
      
      // ✅ NEW: Check if attrs contains cell references (A1, B1, etc.)
      const cellRefPattern = /^[A-Z]+\d+$/;
      const attrsKeys = Object.keys(normalizedParams.attrs);
      const hasCellRefs = attrsKeys.some(key => cellRefPattern.test(key));
      
      if (hasCellRefs) {
        console.log('[SheetsApplication] 🎯 Detected cell reference batch update!');
        return this.handleBatchCellUpdate(normalizedParams.attrs);
      }
      
      // Check common attribute names for cell values
      content = normalizedParams.attrs.value || normalizedParams.attrs.text || normalizedParams.attrs.content;
      console.log('[SheetsApplication] 📦 Extracted content:', content);
    }
    
    // For update/replace operations with content, use custom sheets logic
    if ((normalizedParams.operation === 'update' || normalizedParams.operation === 'replace') && content !== undefined) {
      console.log('[SheetsApplication] 🎯 Using custom updateCellContent');
      return this.updateCellContent({...normalizedParams, content});
    }
    
    // Handle formal batch operations
    if (normalizedParams.operation === 'batch' && normalizedParams.operations && Array.isArray(normalizedParams.operations)) {
      console.log('[SheetsApplication] 🎯 Handling formal batch operation:', {
        operationCount: normalizedParams.operations.length
      });
      return this.handleFormalBatchOperation(normalizedParams.operations);
    }
    
    console.log('[SheetsApplication] ⚠️ No content found, using parent handler');
    
    // For other operations, use parent (auto-save handled by BaseApplication)
    return await super.handleEntityWrite(normalizedParams);
  }
  
  /**
   * Update cell content (text value, not just attributes)
   */
  private async updateCellContent(params: any): Promise<any> {
    console.log('[SheetsApplication] 🔧 updateCellContent:', {
      content: params.content,
      contentLength: params.content?.length,
      target: params.target
    });
    
    await this.buildCoreIndices();
    
    const target = params.target || {};
    const content = String(params.content || '');
    
    // ✅ NEW: Support cellRef (A1 notation)
    if (target.cellRef) {
      console.log('[SheetsApplication] 📍 Using cellRef:', target.cellRef);
      const position = this.parseCellRef(target.cellRef);
      if (position) {
        return this.updateCellAtPosition(position.row, position.col, content);
      }
    }
    
    // ✅ NEW: Support row/col position
    if (target.row !== undefined && target.col !== undefined) {
      console.log('[SheetsApplication] 📍 Using row/col:', target.row, target.col);
      return this.updateCellAtPosition(target.row, target.col, content);
    }
    
    // Existing: Find by node ID
    const nodeId = target.nodeId;
    if (!nodeId) {
      console.error('[SheetsApplication] ❌ No valid target (need cellRef, row/col, or nodeId)');
      throw new Error('No valid target provided for sheets update');
    }
    
    // Find paragraph in table structure
    const paragraph = this.findParagraphInTable(nodeId);
    if (!paragraph) {
      console.error('[SheetsApplication] ❌ Could not find paragraph for nodeId:', nodeId);
      throw new Error(`Could not find cell with ID: ${nodeId}`);
    }
    
    console.log('[SheetsApplication] ✅ Found paragraph, updating text content...');
    
    // Update TEXT content in Y.js transaction
    this.ydoc!.transact(() => {
      // Get or create text node
      if (paragraph.length > 0) {
        const textNode = paragraph.get(0);
        if (textNode instanceof Y.XmlText) {
          // Update existing text
          textNode.delete(0, textNode.length);
          textNode.insert(0, content);
          console.log('[SheetsApplication] ✅ Updated existing text:', content);
        } else {
          // Replace with text node
          paragraph.delete(0);
          const newText = new Y.XmlText();
          newText.insert(0, content);
          paragraph.insert(0, [newText]);
          console.log('[SheetsApplication] ✅ Replaced with new text:', content);
        }
      } else {
        // Create new text node
        const newText = new Y.XmlText();
        newText.insert(0, content);
        paragraph.insert(0, [newText]);
        console.log('[SheetsApplication] ✅ Created new text:', content);
      }
    }, this.ydoc!.clientID);  // ✅ FIX: Use clientID origin for undo tracking
    
    // ✅ Auto-save handled by BaseApplication observer
    console.log('[SheetsApplication] ✅ Cell content updated successfully!');
    
    return {
      affectedNodes: [nodeId],
      stats: {nodesModified: 1}
    };
  }
  
  /**
   * Find paragraph element by node ID (row-sparse)
   */
  private findParagraphInTable(nodeId: string): Y.XmlElement | null {
    if (!this.yrows || this.yrows.size === 0) return null;
    
    console.log('[SheetsApplication] 🔍 Searching row-sparse storage for nodeId:', nodeId);
    
    // Walk all stored rows
    for (const [rowKey, row] of this.yrows.entries()) {
      if (!(row instanceof Y.XmlElement)) continue;
      
      for (let c = 0; c < row.length && c < 20; c++) {
        const cell = row.get(c);
        if (!(cell instanceof Y.XmlElement)) continue;
        
        // Check if cell has this ID
        if (cell.getAttribute('id') === nodeId) {
          const para = cell.get(0);
          if (para instanceof Y.XmlElement) {
            console.log('[SheetsApplication] ✅ Found cell, returning paragraph');
            return para;
          }
        }
        
        // Check if paragraph has this ID
        const para = cell.get(0);
        if (para instanceof Y.XmlElement && para.getAttribute('id') === nodeId) {
          console.log('[SheetsApplication] ✅ Found paragraph directly');
          return para;
        }
      }
    }
    
    console.log('[SheetsApplication] ❌ Paragraph not found in row-sparse storage');
    return null;
  }
  
  
  /**
   * ✅ REMOVED: saveToBackend() and uint8ArrayToBase64()
   * These are inherited from BaseApplication with concurrency protection.
   * No need to duplicate them here.
   */
  
  /**
   * Get application configuration
   */
  protected getConfig(): AppConfig {
    return SHEETS_APP_CONFIG;
  }

  /**
   * Sheets-specific: Get cell by reference (e.g., "A1")
   */
  public getCellByRef(cellRef: string): SerializedNode | undefined {
    // In production, would use gridIndex
    // For now, search by semantic.cellRef or id
    for (const node of this.byIdIndex.values()) {
      if (node.attrs?.semantic?.cellRef === cellRef.toUpperCase()) {
        return node as any;
      }
    }
    return undefined;
  }

  /**
   * Sheets-specific: Get cell value
   */
  public getCellValue(cellRef: string): any {
    const cell = this.getCellByRef(cellRef);
    if (!cell) return undefined;

    // If it's a formula, return calculated value from semantic
    if (cell.attrs?.semantic?.cellType === 'formula') {
      return cell.attrs.value; // Already calculated and stored in value
    }

    return cell.attrs?.value;
  }

  /**
   * Sheets-specific: Set cell value
   */
  public setCellValue(cellRef: string, value: any): void {
    const cell = this.getCellByRef(cellRef);
    if (!cell || !cell.attrs) return;

    cell.attrs.value = value;

    // Update formula engine
    this.formulaEngine.setCellValue(cellRef, value);

    // If this cell is referenced by formulas, recalculate them
    this.recalculateDependents(cellRef);
  }

  /**
   * Sheets-specific: Set cell formula
   */
  public setCellFormula(cellRef: string, formula: string): void {
    const cell = this.getCellByRef(cellRef);
    if (!cell || !cell.attrs?.semantic) return;

    cell.attrs.semantic.cellType = 'formula';
    cell.attrs.semantic.formula = formula;

    // Evaluate formula
    const result = this.evaluateFormula(cellRef, formula);
    cell.attrs.value = result.value;
    cell.attrs.semantic.dependencies = result.dependencies;
    cell.attrs.semantic.error = result.error;

    // Update calculation order
    this.updateCalculationOrder();
  }

  /**
   * Evaluate a formula
   */
  private evaluateFormula(cellRef: string, formula: string): FormulaResult {
    return this.formulaEngine.evaluate(formula);
  }

  /**
   * Recalculate cells that depend on the given cell
   */
  private recalculateDependents(cellRef: string): void {
    // Find all cells that depend on this cell
    const dependents: string[] = [];

    for (const node of this.byIdIndex.values()) {
      const semantic = node.attrs?.semantic;
      if (semantic?.cellType === 'formula' && semantic.dependencies?.includes(cellRef)) {
        if (semantic.cellRef) {
          dependents.push(semantic.cellRef);
        }
      }
    }

    // Recalculate each dependent
    for (const depRef of dependents) {
      const cell = this.getCellByRef(depRef);
      if (cell && cell.attrs?.semantic?.formula) {
        this.setCellFormula(depRef, cell.attrs.semantic.formula);
      }
    }
  }

  /**
   * Update calculation order based on dependencies
   */
  private updateCalculationOrder(): void {
    // Topological sort of formula cells
    // For now, simple implementation
    const formulaCells: string[] = [];

    for (const node of this.byIdIndex.values()) {
      if (node.attrs?.semantic?.cellType === 'formula' && node.attrs.semantic.cellRef) {
        formulaCells.push(node.attrs.semantic.cellRef);
      }
    }

    // Sort by dependency depth (cells with no dependencies first)
    this.calculationOrder = formulaCells.sort((a, b) => {
      const cellA = this.getCellByRef(a);
      const cellB = this.getCellByRef(b);
      const depsA = cellA?.attrs?.semantic?.dependencies?.length || 0;
      const depsB = cellB?.attrs?.semantic?.dependencies?.length || 0;
      return depsA - depsB;
    });
  }

  /**
   * Recalculate all formulas
   */
  public recalculateAll(): void {
    // First, populate formula engine with all cell values
    for (const node of this.byIdIndex.values()) {
      const semantic = node.attrs?.semantic;
      if (semantic?.cellRef && semantic.cellType !== 'formula') {
        this.formulaEngine.setCellValue(semantic.cellRef, node.attrs.value);
      }
    }

    // Then calculate formulas in dependency order
    for (const cellRef of this.calculationOrder) {
      const cell = this.getCellByRef(cellRef);
      if (cell && cell.attrs?.semantic?.formula) {
        const result = this.evaluateFormula(cellRef, cell.attrs.semantic.formula);
        if (cell.attrs) {
          cell.attrs.value = result.value;
          cell.attrs.semantic.error = result.error;
        }
        
        // Update formula engine with calculated value
        this.formulaEngine.setCellValue(cellRef, result.value);
      }
    }
  }

  /**
   * Sheets-specific: Get cells in range (e.g., "A1:B10")
   */
  public getCellsInRange(range: string): SerializedNode[] {
    // Parse range (e.g., "A1:B10")
    const [start, end] = range.split(':');
    
    // In production, would use gridIndex for efficient range queries
    // For now, return all cells (simplified)
    return Array.from(this.byIdIndex.values()).filter(node => 
      isCellClassName(node.attrs?.className || '')
    ) as any;
  }

  /**
   * Sheets-specific: Get all formula cells
   */
  public getAllFormulaCells(): SerializedNode[] {
    const nodeIds = this.byClassIndex.get('cell-formula');
    if (!nodeIds) return [];

    return Array.from(nodeIds)
      .map(id => this.byIdIndex.get(id))
      .filter((node): node is any => node !== undefined);
  }

  /**
   * Sheets-specific: Get spreadsheet statistics
   */
  public async getSheetStats() {
    // Ensure indices are up-to-date
    await this.buildCoreIndices();
    
    const allCells = Array.from(this.byIdIndex.values()).filter(node => 
      isCellClassName(node.attrs?.className || '')
    );
    
    return {
      totalCells: allCells.length,
      formulaCells: this.getAllFormulaCells().length,
      textCells: this.getNodesByClassName('cell-text').length,
      numberCells: this.getNodesByClassName('cell-number').length,
      dateCells: this.getNodesByClassName('cell-date').length,
      emptyCells: allCells.filter(cell => !cell.attrs?.value).length,
      errorCells: this.getNodesByClassName('cell-error').length,
    };
  }

  /**
   * Sheets-specific: Export to CSV
   */
  public async exportToCSV(): Promise<string> {
    // Ensure indices are up-to-date before export
    await this.buildCoreIndices();
    
    // Get all cells
    const cells = Array.from(this.byIdIndex.values()).filter(node => 
      isCellClassName(node.attrs?.className || '')
    );

    // Group by row
    const rows = new Map<number, Map<number, any>>();
    
    for (const node of cells) {
      const semantic = node.attrs?.semantic;
      if (!semantic?.cellRef) continue;

      // Parse cell reference (simplified - assumes A1 notation)
      // In production, would use parseA1Notation from grid/types
      const match = semantic.cellRef.match(/^([A-Z]+)(\d+)$/);
      if (!match) continue;

      const col = match[1].charCodeAt(0) - 65; // A=0, B=1, etc.
      const row = parseInt(match[2]) - 1;

      if (!rows.has(row)) {
        rows.set(row, new Map());
      }

      rows.get(row)!.set(col, node.attrs.value ?? '');
    }

    // Convert to CSV
    const csvLines: string[] = [];
    const sortedRows = Array.from(rows.keys()).sort((a, b) => a - b);

    for (const rowNum of sortedRows) {
      const rowCells = rows.get(rowNum)!;
      const sortedCols = Array.from(rowCells.keys()).sort((a, b) => a - b);
      const maxCol = Math.max(...sortedCols);

      const csvRow: string[] = [];
      for (let col = 0; col <= maxCol; col++) {
        const value = rowCells.get(col) ?? '';
        const escaped = String(value).includes(',') || String(value).includes('"')
          ? `"${String(value).replace(/"/g, '""')}"`
          : String(value);
        csvRow.push(escaped);
      }

      csvLines.push(csvRow.join(','));
    }

    return csvLines.join('\n');
  }

  /**
   * Sheets-specific: Find cells with errors
   */
  public getCellsWithErrors(): SerializedNode[] {
    const results: SerializedNode[] = [];

    for (const node of this.byIdIndex.values()) {
      if (node.attrs?.semantic?.error || node.attrs?.className === 'cell-error') {
        results.push(node as any);
      }
    }

    return results;
  }

  /**
   * Sheets-specific: Get cell dependencies graph
   */
  public getDependencyGraph(): Map<string, string[]> {
    const graph = new Map<string, string[]>();

    for (const node of this.byIdIndex.values()) {
      const semantic = node.attrs?.semantic;
      if (semantic?.cellType === 'formula' && semantic.cellRef) {
        graph.set(semantic.cellRef, semantic.dependencies || []);
      }
    }

    return graph;
  }
  
  // ========================================================================
  // Sheets-Specific Find & Replace (Override Universal Methods)
  // ========================================================================
  
  /**
   * ✅ OVERRIDE: Sheets-specific find
   * Searches cell values with A1 notation and row/col positions
   */
  async handleFind(query: string, options?: {
    matchCase?: boolean;
    matchWholeWord?: boolean;
    searchIn?: 'content' | 'ids' | 'all';
  }): Promise<import('../../../services/baseApplication/BaseApplication').FindResult[]> {
    console.log('[SheetsApplication] 🔍 Sheets Find in cells:', query, options);
    
    if (!query || query.trim() === '') {
      return [];
    }
    
    const results: import('../../../services/baseApplication/BaseApplication').FindResult[] = [];
    const searchLower = options?.matchCase ? query : query.toLowerCase();
    
    // Rebuild indices to ensure fresh data
    await this.buildCoreIndices();
    
    // Search all cells in row-sparse storage
    // yrows values are Y.XmlElement('table_row') nodes — iterate children to find cells
    if (this.yrows && this.yrows.size > 0) {
      console.log('[SheetsApplication] 🔍 Searching row-sparse storage...');

      for (const [rowKey, rowEl] of this.yrows.entries()) {
        if (!(rowEl instanceof Y.XmlElement)) continue;
        const rowIdx = parseInt(rowKey, 10);

        for (let ci = 0; ci < rowEl.length; ci++) {
          const cell = rowEl.get(ci);
          if (!(cell instanceof Y.XmlElement)) continue;
          const colAttr = cell.getAttribute('col');
          const colIdx = colAttr != null ? parseInt(colAttr, 10) : ci;

          // Read text from paragraph → XmlText child
          let cellValue = '';
          for (let pi = 0; pi < cell.length; pi++) {
            const para = cell.get(pi);
            if (!(para instanceof Y.XmlElement)) continue;
            for (let ti = 0; ti < para.length; ti++) {
              const t = para.get(ti);
              if (t instanceof Y.XmlText) cellValue += t.toString();
            }
          }

          if (!cellValue) continue;

          const searchText = options?.matchCase ? cellValue : cellValue.toLowerCase();
          const searchTerm = options?.matchCase ? query : searchLower;

          if (searchText.includes(searchTerm)) {
            const matchCount = this.countMatches(cellValue, query, options);
            if (matchCount > 0) {
              results.push({
                nodeId: `cell-${rowIdx}-${colIdx}`,
                text: cellValue,
                matchCount,
                className: 'cell',
                position: { row: rowIdx, col: colIdx },
              });
            }
          }
        }
      }
    }
    
    // Also search in legacy byIdIndex if exists
    for (const [nodeId, node] of this.byIdIndex.entries()) {
      const cellValue = node.attrs?.value || '';
      
      if (!cellValue) continue;
      
      const valueStr = String(cellValue);
      const searchText = options?.matchCase ? valueStr : valueStr.toLowerCase();
      const searchTerm = options?.matchCase ? query : searchLower;
      
      if (searchText.includes(searchTerm)) {
        const matchCount = this.countMatches(valueStr, query, options);
        
        if (matchCount > 0) {
          results.push({
            nodeId,
            text: valueStr,
            matchCount,
            className: node.attrs?.className || 'cell',
            position: {
              row: node.attrs?.semantic?.row,
              col: node.attrs?.semantic?.col,
            },
          });
        }
      }
    }
    
    console.log('[SheetsApplication] ✅ Sheets Find complete:', results.length, 'results');
    return results;
  }
  
  /**
   * ✅ OVERRIDE: Sheets-specific replace
   * Updates cell values directly in row-sparse storage
   */
  async handleReplace(params: {
    find: string;
    replace: string;
    nodeIds?: string[];
    matchCase?: boolean;
    replaceAll?: boolean;
  }): Promise<import('../../../services/baseApplication/BaseApplication').ReplaceResult> {
    console.log('[SheetsApplication] 🔄 Sheets Replace in cells:', params);
    
    if (!this.ydoc) {
      throw new Error('Y.js not initialized - call initialize() first');
    }
    
    const { find, replace, nodeIds, matchCase, replaceAll } = params;
    const affectedNodes: string[] = [];
    
    // Find all matching cells
    let matches: import('../../../services/baseApplication/BaseApplication').FindResult[];
    
    if (nodeIds && nodeIds.length > 0) {
      // Search only specific cells
      const allMatches = await this.handleFind(find, { matchCase });
      matches = allMatches.filter(m => nodeIds.includes(m.nodeId));
    } else {
      // Search all cells
      matches = await this.handleFind(find, { matchCase });
    }
    
    console.log('[SheetsApplication] 🎯 Found', matches.length, 'cells to replace');
    
    // Build regex for replacement
    const flags = matchCase ? (replaceAll ? 'g' : '') : (replaceAll ? 'gi' : 'i');
    const escapedFind = find.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escapedFind, flags);

    // Execute replacements in Y.js transaction
    // yrows values are Y.XmlElement('table_row') — navigate children to find the target cell
    this.ydoc.transact(() => {
      for (const match of matches) {
        if (!match.position || match.position.row === undefined || match.position.col === undefined) {
          continue;
        }

        const row = match.position.row;
        const col = match.position.col;

        const rowEl = this.yrows?.get(String(row));
        if (!(rowEl instanceof Y.XmlElement)) continue;

        // Find the cell by col attribute
        let targetCell: Y.XmlElement | null = null;
        for (let ci = 0; ci < rowEl.length; ci++) {
          const cell = rowEl.get(ci);
          if (!(cell instanceof Y.XmlElement)) continue;
          const colAttr = cell.getAttribute('col');
          const cellCol = colAttr != null ? parseInt(colAttr, 10) : ci;
          if (cellCol === col) { targetCell = cell; break; }
        }
        if (!targetCell) continue;

        // Find paragraph → XmlText
        let textNode: Y.XmlText | null = null;
        for (let pi = 0; pi < targetCell.length; pi++) {
          const para = targetCell.get(pi);
          if (!(para instanceof Y.XmlElement)) continue;
          for (let ti = 0; ti < para.length; ti++) {
            const t = para.get(ti);
            if (t instanceof Y.XmlText) { textNode = t; break; }
          }
          if (textNode) break;
        }
        if (!textNode) continue;

        const oldValue = textNode.toString();
        const newValue = oldValue.replace(regex, replace);

        if (newValue !== oldValue) {
          textNode.delete(0, oldValue.length);
          textNode.insert(0, newValue);
          affectedNodes.push(match.nodeId);
        }

        if (!replaceAll) break;
      }
    }, this.ydoc.clientID);
    
    // Rebuild indices and save
    await this.buildCoreIndices();
    await this.saveToBackend();
    
    console.log('[SheetsApplication] ✅ Sheets Replace complete:', affectedNodes.length, 'cells affected');
    
    return {
      affectedNodes,
      replacementCount: affectedNodes.length,
    };
  }
  
  /**
   * ✅ OVERRIDE: Sheets-specific entity_format
   * Apply formatting to cells using cell references (A1, B2, etc.)
   */
  async handleEntityFormat(target: any, format: any): Promise<{ affectedNodes: string[]; affectedCells: string[] }> {
    console.log('[SheetsApplication] handleEntityFormat (sheets-specific):', { target, format });
    
    if (!this.ydoc) {
      throw new Error('Y.js not initialized - call load() first');
    }
    
    if (!this.yrows) {
      throw new Error('Row storage not initialized');
    }
    
    await this.buildCoreIndices();
    
    // 1. Parse target to get cell coordinates
    const cellCoords = this.parseCellTarget(target);
    console.log('[SheetsApplication] Parsed cell coordinates:', cellCoords);
    
    if (cellCoords.length === 0) {
      console.warn('[SheetsApplication] No cells matched target:', target);
      return { affectedNodes: [], affectedCells: [] };
    }
    
    // 2. Map format properties to cell attributes
    const cellAttrs = this.mapFormatToAttributes(format);
    console.log('[SheetsApplication] Mapped attributes:', cellAttrs);
    
    const affectedCells: string[] = [];
    
    // 3. Apply formatting in Y.js transaction
    this.ydoc.transact(() => {
      for (const { row, col } of cellCoords) {
        // Ensure cell exists (create if needed)
        let cellElement = this.getCellElementAt(row, col);
        
        if (!cellElement) {
          console.warn(`[SheetsApplication] Cell not found at row ${row}, col ${col}, creating...`);
          this.ensureCellExists(row, col);
          cellElement = this.getCellElementAt(row, col);
          
          if (!cellElement) {
            console.error(`[SheetsApplication] Failed to create cell at row ${row}, col ${col}`);
            continue;
          }
        }
        
        // Apply each attribute
        Object.entries(cellAttrs).forEach(([key, value]) => {
          cellElement!.setAttribute(key, value);
        });
        
        const cellRef = this.getCellRef(row, col);
        affectedCells.push(cellRef);
        console.log(`[SheetsApplication] ✅ Applied formatting to ${cellRef}`);
      }
    }, this.ydoc.clientID);
    
    // 4. Rebuild indices and save
    await this.buildCoreIndices();
    await this.saveToBackend();
    
    console.log('[SheetsApplication] ✅ Applied formatting to', affectedCells.length, 'cells');
    
    // Return both formats for compatibility
    return { affectedNodes: affectedCells, affectedCells };
  }
  
  /**
   * Parse cell target into array of { row, col } coordinates
   */
  private parseCellTarget(target: any): Array<{ row: number; col: number }> {
    const coords: Array<{ row: number; col: number }> = [];
    
    // Single cell: { cell: "A1" }
    if (target.cell) {
      const coord = this.parseCellRef(target.cell);
      if (coord) coords.push(coord);
    }
    
    // Multiple cells: { cells: ["A1", "B2", "C3"] }
    else if (target.cells && Array.isArray(target.cells)) {
      for (const cellRef of target.cells) {
        const coord = this.parseCellRef(cellRef);
        if (coord) coords.push(coord);
      }
    }
    
    // Range: { range: "A1:C5" }
    else if (target.range) {
      const rangeParts = target.range.split(':');
      if (rangeParts.length === 2) {
        const start = this.parseCellRef(rangeParts[0]);
        const end = this.parseCellRef(rangeParts[1]);
        
        if (start && end) {
          for (let r = start.row; r <= end.row; r++) {
            for (let c = start.col; c <= end.col; c++) {
              coords.push({ row: r, col: c });
            }
          }
        }
      }
    }
    
    // Entire row: { row: 5 }
    else if (target.row !== undefined) {
      const rowNum = target.row;
      for (let c = 0; c < 20; c++) {  // 20 columns (A-T)
        coords.push({ row: rowNum, col: c });
      }
    }
    
    // Entire column: { column: "B" }
    else if (target.column) {
      const col = this.parseColumnLetter(target.column);
      if (col !== -1 && this.yrows) {
        // Get max row from existing data
        const maxRow = Array.from(this.yrows.keys()).reduce((max, key) => {
          const rowNum = parseInt(key, 10);
          return Math.max(max, rowNum);
        }, 0);
        
        for (let r = 0; r <= Math.max(maxRow, 99); r++) {  // At least 100 rows
          coords.push({ row: r, col });
        }
      }
    }
    
    return coords;
  }
  
  /**
   * Get cell element at specific position (helper for formatting)
   */
  private getCellElementAt(row: number, col: number): Y.XmlElement | null {
    if (!this.yrows) return null;
    
    const rowKey = row.toString();
    const rowElement = this.yrows.get(rowKey) as Y.XmlElement | undefined;
    
    if (!rowElement || !(rowElement instanceof Y.XmlElement)) {
      return null;
    }
    
    if (col >= rowElement.length) {
      return null;
    }
    
    const cell = rowElement.get(col);
    if (cell instanceof Y.XmlElement) {
      return cell;
    }
    
    return null;
  }
  
  /**
   * Parse column letter(s) to column index (A=0, B=1, ..., Z=25, AA=26, etc.)
   * (Helper for parseCellTarget - uses existing multi-letter parsing logic)
   */
  private parseColumnLetter(letters: string): number {
    let col = 0;
    for (let i = 0; i < letters.length; i++) {
      col = col * 26 + (letters.charCodeAt(i) - 65 + 1);
    }
    return col - 1;  // Convert to 0-based
  }
  
  /**
   * Map format properties to cell attributes
   */
  private mapFormatToAttributes(format: any): Record<string, any> {
    const attrs: Record<string, any> = {};
    
    // Direct mappings
    if (format.backgroundColor !== undefined) attrs.backgroundColor = format.backgroundColor;
    if (format.fontSize !== undefined) attrs.fontSize = format.fontSize;
    if (format.fontFamily !== undefined) attrs.fontFamily = format.fontFamily;
    if (format.textAlign !== undefined) attrs.textAlign = format.textAlign;
    if (format.numberFormat !== undefined) attrs.numberFormat = format.numberFormat;
    if (format.bold !== undefined) attrs.bold = format.bold;
    if (format.italic !== undefined) attrs.italic = format.italic;
    if (format.underline !== undefined) attrs.underline = format.underline;
    if (format.strikethrough !== undefined) attrs.strikethrough = format.strikethrough;
    
    // Map CSS color to textColor
    if (format.color !== undefined) attrs.textColor = format.color;
    if (format.textColor !== undefined) attrs.textColor = format.textColor;
    
    // Map CSS font-weight to bold
    if (format.fontWeight === 'bold' || format.fontWeight >= 600) attrs.bold = true;
    if (format.fontWeight === 'normal' || format.fontWeight < 600) attrs.bold = false;
    
    // Map CSS font-style to italic
    if (format.fontStyle === 'italic') attrs.italic = true;
    if (format.fontStyle === 'normal') attrs.italic = false;
    
    // Map CSS text-decoration
    if (format.textDecoration === 'underline') attrs.underline = true;
    if (format.textDecoration === 'line-through') attrs.strikethrough = true;
    if (format.textDecoration === 'none') {
      attrs.underline = false;
      attrs.strikethrough = false;
    }
    
    return attrs;
  }
  
  /**
   * Ensure a cell exists at the given row and column
   */
  private ensureCellExists(row: number, col: number): void {
    if (!this.yrows) {
      console.error('[SheetsApplication] yrows is null, cannot create cell');
      return;
    }
    
    const rowKey = row.toString();
    
    // Get or create row
    let rowElement = this.yrows.get(rowKey) as Y.XmlElement | undefined;
    
    if (!rowElement) {
      // Create new row
      rowElement = new Y.XmlElement('table_row'); // ✅ BUG FIX: Use 'table_row' for consistency
      rowElement.setAttribute('id', this.generateId()); // ✅ FIX: Ensure row has ID
      this.yrows.set(rowKey, rowElement);
      console.log(`[SheetsApplication] Created new row ${row}`);
    }
    
    // Ensure row has enough cells
    while (rowElement.length <= col) {
      const cellId = `cell_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const cell = new Y.XmlElement('table_cell');
      cell.setAttribute('id', cellId);
      cell.setAttribute('cellType', 'text');
      
      // Create paragraph with empty text
      const para = new Y.XmlElement('paragraph');
      para.setAttribute('id', this.generateId()); // ✅ FIX: Ensure paragraph has ID
      const text = new Y.XmlText();
      para.insert(0, [text]);
      cell.insert(0, [para]);
      
      rowElement.push([cell]);
    }
  }
  
  /**
   * Override destroy to force save and cleanup
   * ✅ FIXED: Now async to match BaseApplication
   */
  async destroy(): Promise<void> {
    console.log('[SheetsApplication] Destroying...');
    
    // ✅ BaseApplication.destroy() handles:
    // - Force final save (awaited properly)
    // - Cleanup persistence (remove observers, cancel debounced saves)
    // - Collaboration cleanup
    // - Y.Doc cleanup
    await super.destroy();
    
    console.log('[SheetsApplication] ✅ Destroyed');
  }

  /**
   * ==================================================================
   * DELETE OPERATION HELPERS
   * ==================================================================
   */

  /**
   * Convert column index to string (0=A, 1=B, ..., 25=Z, 26=AA, ...)
   */
  private indexToColumnStr(col: number): string {
    let result = '';
    let num = col + 1;
    while (num > 0) {
      const mod = (num - 1) % 26;
      result = String.fromCharCode(65 + mod) + result;
      num = Math.floor((num - 1) / 26);
    }
    return result;
  }

  /**
   * Convert column letter to index (A=0, B=1, ..., Z=25, AA=26, ...)
   */
  private columnToIndex(col: string): number {
    let index = 0;
    for (let i = 0; i < col.length; i++) {
      index = index * 26 + (col.charCodeAt(i) - 65 + 1);
    }
    return index - 1;
  }

  /**
   * Convert column index to letter (0=A, 1=B, ..., 25=Z, 26=AA, ...)
   */
  private indexToColumn(index: number): string {
    let col = '';
    index += 1;
    while (index > 0) {
      const mod = (index - 1) % 26;
      col = String.fromCharCode(65 + mod) + col;
      index = Math.floor((index - mod) / 26);
    }
    return col;
  }

  /**
   * Expand cell range to individual cell references
   * @example "D1:D10" → ["D1", "D2", ..., "D10"]
   * @example "A1" → ["A1"]
   */
  private expandCellRange(cellRef: string): string[] {
    console.log('[SheetsApplication] 📍 expandCellRange called:', cellRef);
    
    // Single cell (no colon)
    if (!cellRef.includes(':')) {
      console.log('[SheetsApplication] Single cell reference:', cellRef);
      const parsed = this.parseCellRef(cellRef);
      if (!parsed) {
        console.warn('[SheetsApplication] Invalid single cell reference:', cellRef);
        return [];
      }
      return [cellRef];
    }
    
    // Parse range "D1:D10"
    const [start, end] = cellRef.split(':');
    console.log('[SheetsApplication] Parsing range:', { start, end });
    
    const startPos = this.parseCellRef(start);
    const endPos = this.parseCellRef(end);
    
    console.log('[SheetsApplication] Parsed positions:', { startPos, endPos });
    
    // If either endpoint is invalid, try to clamp to valid bounds
    if (!startPos || !endPos) {
      console.warn('[SheetsApplication] 🔧 Range contains invalid cells, attempting to clamp to valid bounds');
      
      // Parse without bounds checking
      const startMatch = start.match(/^([A-Z]+)(\d+)$/);
      const endMatch = end.match(/^([A-Z]+)(\d+)$/);
      
      if (!startMatch || !endMatch) {
        console.error('[SheetsApplication] Invalid cellRef format:', cellRef);
        return [];
      }
      
      // Parse column letters
      let startCol = 0;
      for (let i = 0; i < startMatch[1].length; i++) {
        startCol = startCol * 26 + (startMatch[1].charCodeAt(i) - 65 + 1);
      }
      startCol -= 1;  // 0-indexed
      
      let endCol = 0;
      for (let i = 0; i < endMatch[1].length; i++) {
        endCol = endCol * 26 + (endMatch[1].charCodeAt(i) - 65 + 1);
      }
      endCol -= 1;  // 0-indexed
      
      // Parse rows
      let startRow = parseInt(startMatch[2]) - 1;  // 0-indexed
      let endRow = parseInt(endMatch[2]) - 1;
      
      // Clamp to valid bounds (500 rows x 20 cols)
      const origStartRow = startRow, origStartCol = startCol;
      const origEndRow = endRow, origEndCol = endCol;
      
      startRow = Math.max(0, Math.min(startRow, 499));
      startCol = Math.max(0, Math.min(startCol, 19));
      endRow = Math.max(0, Math.min(endRow, 499));
      endCol = Math.max(0, Math.min(endCol, 19));
      
      console.warn('[SheetsApplication] ⚠️ Clamped range to valid bounds:', {
        original: { startRow: origStartRow, startCol: origStartCol, endRow: origEndRow, endCol: origEndCol },
        clamped: { startRow, startCol, endRow, endCol },
        originalRange: cellRef,
        clampedRange: `${this.indexToColumnStr(startCol)}${startRow + 1}:${this.indexToColumnStr(endCol)}${endRow + 1}`
      });
      
      // Generate cells in clamped range
      const cells: string[] = [];
      for (let row = startRow; row <= endRow; row++) {
        for (let col = startCol; col <= endCol; col++) {
          cells.push(`${this.indexToColumnStr(col)}${row + 1}`);
        }
      }
      
      console.log('[SheetsApplication] 📋 Expanded clamped range:', { 
        inputRange: cellRef,
        cellCount: cells.length,
        firstCell: cells[0],
        lastCell: cells[cells.length - 1]
      });
      
      return cells;
    }
    
    // Both positions valid, generate cells normally
    const cells: string[] = [];
    for (let row = startPos.row; row <= endPos.row; row++) {
      for (let col = startPos.col; col <= endPos.col; col++) {
        cells.push(`${this.indexToColumnStr(col)}${row + 1}`);
      }
    }
    
    console.log('[SheetsApplication] 📋 Expanded range complete:', { 
      inputRange: cellRef,
      cellCount: cells.length,
      firstCell: cells[0],
      lastCell: cells[cells.length - 1],
      sampleCells: cells.slice(0, 5)
    });
    
    return cells;
  }

  /**
   * Clear a single cell's content
   * @returns true if cell was cleared, false if cell didn't exist
   */
  private clearCell(cellRef: string): boolean {
    const parsed = this.parseCellRef(cellRef);
    if (!parsed) {
      console.warn('[SheetsApplication] Invalid cellRef:', cellRef);
      return false;
    }
    
    const { col, row } = parsed;
    const rowKey = row.toString();  // Just the number: "0", "1", "2", ...
    
    console.log('[SheetsApplication] Clearing cell:', { cellRef, row, col, rowKey });
    
    // Get row
    const rowElement = this.yrows?.get(rowKey);
    if (!rowElement) {
      console.log('[SheetsApplication] Row does not exist:', row);
      return false;
    }
    
    // Get cell by numeric index (cells are stored as array children)
    if (col >= rowElement.length) {
      console.log('[SheetsApplication] Cell index out of bounds:', { cellRef, col, rowLength: rowElement.length });
      return false;
    }
    
    const cellElement = rowElement.get(col) as Y.XmlElement | undefined;
    if (!cellElement || !(cellElement instanceof Y.XmlElement)) {
      console.log('[SheetsApplication] Cell does not exist:', cellRef);
      return false;
    }
    
    // Clear cell content (set paragraph text to empty)
    const paragraph = cellElement.get(0) as Y.XmlElement | undefined;
    if (paragraph && paragraph.firstChild) {
      const textNode = paragraph.firstChild as Y.XmlText;
      if (textNode.length > 0) {
        textNode.delete(0, textNode.length);
        console.log('[SheetsApplication] ✅ Cleared cell:', cellRef);
        return true;
      }
    }
    
    console.log('[SheetsApplication] Cell already empty:', cellRef);
    return true;  // Still count as success
  }

  /**
   * Clear all cells in a row
   * @returns number of cells cleared
   */
  private clearRow(row: number): number {
    const rowKey = row.toString();  // Just the number: "0", "1", "2", ...
    const rowElement = this.yrows?.get(rowKey);
    
    if (!rowElement) {
      console.log('[SheetsApplication] Row does not exist:', row);
      return 0;
    }
    
    console.log('[SheetsApplication] Clearing row:', { row, cellCount: rowElement.length });
    
    let cleared = 0;
    // Iterate cells by numeric index (cells are stored as array children)
    for (let col = 0; col < rowElement.length; col++) {
      const cellElement = rowElement.get(col);
      if (cellElement instanceof Y.XmlElement) {
        const paragraph = cellElement.get(0) as Y.XmlElement | undefined;
        if (paragraph && paragraph.firstChild) {
          const textNode = paragraph.firstChild as Y.XmlText;
          if (textNode.length > 0) {
            textNode.delete(0, textNode.length);
            cleared++;
          }
        }
      }
    }
    
    console.log('[SheetsApplication] ✅ Cleared row:', { row, cells: cleared });
    return cleared;
  }

  /**
   * Clear all cells in a column
   * @returns number of cells cleared
   */
  private clearColumn(col: string): number {
    const colIndex = this.columnToIndex(col);
    console.log('[SheetsApplication] Clearing column:', { col, colIndex });
    
    let cleared = 0;
    // Iterate all rows
    this.yrows?.forEach((rowElement: any, rowKey: string) => {
      // Get cell by numeric index (cells are stored as array children)
      if (colIndex < rowElement.length) {
        const cellElement = rowElement.get(colIndex);
        if (cellElement instanceof Y.XmlElement) {
          const paragraph = cellElement.get(0) as Y.XmlElement | undefined;
          if (paragraph && paragraph.firstChild) {
            const textNode = paragraph.firstChild as Y.XmlText;
            if (textNode.length > 0) {
              textNode.delete(0, textNode.length);
              cleared++;
            }
          }
        }
      }
    });
    
    console.log('[SheetsApplication] ✅ Cleared column:', { col, colIndex, cells: cleared });
    return cleared;
  }
}

